/** @graphql agg **/
import gql from 'graphql-tag';
import { useCallback } from 'react';
import { useMutationInterop } from '@confluence/apollo-relay-interop';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { containsNoNetworkError } from '@confluence/network';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

import type {
	useMarkCommentsAsReadMutation as useMarkCommentsAsReadMutation$data,
	useMarkCommentsAsReadMutationVariables as useMarkCommentsAsReadMutation$variables,
} from './__types__/useMarkCommentsAsReadMutation';

const markCommentsAsReadMutation = gql`
	mutation useMarkCommentsAsReadMutation($commentIds: [ID!]!) {
		markCommentsAsRead(input: { commentIds: $commentIds }) {
			success
			errors
		}
	}
`;

export const useMarkCommentsAsRead = () => {
	const [markCommentsAsRead] = useMutationInterop<
		useMarkCommentsAsReadMutation$data,
		useMarkCommentsAsReadMutation$variables
	>(markCommentsAsReadMutation);

	const markCommentsAsReadFn = useCallback(
		async (commentIds: string[]) => {
			if (commentIds.length === 0) {
				return;
			}

			try {
				await markCommentsAsRead({
					variables: { commentIds },
				});
			} catch (err) {
				// Handle expected errors (403/unauthorized, network errors) - just mark as handled
				if (isUnauthorizedError(err) || containsNoNetworkError(err)) {
					markErrorAsHandled(err);
					return;
				}

				// Handle unexpected errors (including 500) - track them with context
				setContentErrorAttributes(err, {
					objectType: 'comment',
				});

				getMonitoringClient().submitError(err, {
					attribution: Attribution.COMMENTS,
				});

				markErrorAsHandled(err);
			}
		},
		[markCommentsAsRead],
	);

	return markCommentsAsReadFn;
};
